/**
 * ComplianceTaskModal — Shared edit/create modal for staff operational tasks.
 * Used by both the Command Center (ComplianceTabs) and Task module (Staff tab).
 *
 * Props:
 *   opened    — boolean, controls modal visibility
 *   onClose   — function, called when modal closes
 *   editTask  — object | null, task being edited (null = create mode)
 *   addLoading— boolean, loading state for the save button
 *   allStaff  — array, staff data from Redux [{id, firstName, lastName, email, ...}]
 *   onSaved   — function, called after successful create/update
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box, Button, Flex, Group, Modal, MultiSelect, NumberInput,
  Select, Text, Textarea, TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  IconClipboardCheck, IconCalendar, IconListCheck,
  IconSettings, IconNote, IconSubtask,
} from "@tabler/icons-react";
import moment from "moment";
import swal from "sweetalert";
import {
  createOperationalTask,
  updateOperationalTask,
} from "../../services/compliance";
import { getAllNoteTypes } from "../../services/note";
import { getTaskTypes } from "../../services/task";

const ComplianceTaskModal = ({ opened, onClose, editTask, addLoading, allStaff, onSaved }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [scheduleType, setScheduleType] = useState("one_time");
  const [frequency, setFrequency] = useState("daily");
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState("09:00 AM");
  const [dueDate, setDueDate] = useState(null);
  const [status, setStatus] = useState("not_started");

  // System task fields
  const [taskType, setTaskType] = useState("admin");
  const [systemCategory, setSystemCategory] = useState(null);
  const [linkedNoteType, setLinkedNoteType] = useState(null);
  const [linkedTaskType, setLinkedTaskType] = useState(null);

  // Note/Task types from Redux
  const noteState = useSelector((state) => state.note);
  const taskState = useSelector((state) => state.task);
  const rawNoteTypes = noteState?.allNotesTypes;
  const noteTypesList = Array.isArray(rawNoteTypes) ? rawNoteTypes : Array.isArray(rawNoteTypes?.ntypes) ? rawNoteTypes.ntypes : [];
  const taskTypesList = Array.isArray(taskState?.taskTypes) ? taskState.taskTypes : [];

  // Load note/task types when system task type is selected
  useEffect(() => {
    if (taskType === "system" && opened) {
      if (!noteTypesList.length) getAllNoteTypes();
      if (!taskTypesList.length) getTaskTypes();
    }
  }, [taskType, opened]);

  useEffect(() => {
    if (editTask) {
      setTitle(editTask.title || "");
      setDescription(editTask.description || "");
      setSelectedStaff((editTask.staff || []).map((s) => s._id || s.id || s));
      setScheduleType(editTask.scheduleType || "one_time");
      setStatus(editTask.status || "not_started");
      if (editTask.scheduleType === "recurring" && editTask.recurrence) {
        setFrequency(editTask.recurrence.frequency || "daily");
        setDayOfWeek(editTask.recurrence.dayOfWeek ?? 1);
        setDayOfMonth(editTask.recurrence.dayOfMonth ?? 1);
        const storedTime = editTask.recurrence.timeOfDay || "09:00 AM";
        if (!storedTime.includes("AM") && !storedTime.includes("PM")) {
          const [hh, mm] = storedTime.split(":").map(Number);
          const period = hh >= 12 ? "PM" : "AM";
          const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
          setTimeOfDay(`${String(h12).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${period}`);
        } else {
          setTimeOfDay(storedTime);
        }
      }
      if (editTask.dueDate) setDueDate(new Date(editTask.dueDate));
      // System task fields
      setTaskType(editTask.taskType || "admin");
      setSystemCategory(editTask.systemCategory || null);
      setLinkedNoteType(editTask.linkedNoteType?._id || editTask.linkedNoteType || null);
      setLinkedTaskType(editTask.linkedTaskType?._id || editTask.linkedTaskType || null);
    } else {
      setTitle(""); setDescription(""); setSelectedStaff([]);
      setScheduleType("one_time"); setFrequency("daily");
      setDayOfWeek(1); setDayOfMonth(1); setTimeOfDay("09:00 AM");
      setDueDate(null); setStatus("not_started");
      setTaskType("admin"); setSystemCategory(null);
      setLinkedNoteType(null); setLinkedTaskType(null);
    }
  }, [editTask, opened]);

  const staffData = (allStaff || [])
    .filter((s) => !s.deleted && s.active !== false && s.role !== "contractor")
    .map((s) => ({
      value: s.id || s._id,
      label: `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.email,
    }));

  const handleSubmit = async () => {
    if (!title.trim()) return swal("Error", "Title is required", "error");
    if (!selectedStaff.length) return swal("Error", "Assign at least one staff member", "error");

    // Validate system task fields
    if (taskType === "system") {
      if (!systemCategory) return swal("Error", "Select a system category (Note or Task)", "error");
      if (systemCategory === "note" && !linkedNoteType) return swal("Error", "Select a note type", "error");
      if (systemCategory === "task" && !linkedTaskType) return swal("Error", "Select a task type", "error");
    }

    const payload = { title, description, staff: selectedStaff, scheduleType, status, taskType };

    // Add system task fields
    if (taskType === "system") {
      payload.systemCategory = systemCategory;
      if (systemCategory === "note") payload.linkedNoteType = linkedNoteType;
      if (systemCategory === "task") payload.linkedTaskType = linkedTaskType;
    }

    if (scheduleType === "recurring") {
      let time24 = timeOfDay;
      const match = timeOfDay.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (match) {
        let h = parseInt(match[1]);
        const m = match[2];
        const period = match[3].toUpperCase();
        if (period === "PM" && h !== 12) h += 12;
        if (period === "AM" && h === 12) h = 0;
        time24 = `${String(h).padStart(2, "0")}:${m}`;
      }
      payload.recurrence = { frequency, timeOfDay: time24 };
      if (frequency === "weekly") payload.recurrence.dayOfWeek = dayOfWeek;
      if (frequency === "monthly") payload.recurrence.dayOfMonth = dayOfMonth;
    } else {
      if (!dueDate) return swal("Error", "Due date is required", "error");
      payload.dueDate = dueDate;
    }

    let result;
    if (editTask) {
      result = await updateOperationalTask(editTask._id, payload);
    } else {
      result = await createOperationalTask(payload);
    }

    if (result) {
      onClose();
      if (onSaved) onSaved();
    }
  };

  // ─── Helpers ───
  const getInitials = (label) => {
    const parts = label.split(" ").filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return (parts[0] || "?")[0].toUpperCase();
  };
  const avatarColors = ["#7C3AED", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#8B5CF6", "#06B6D4"];
  const getAvatarColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  const fieldStyles = {
    label: { fontFamily: "Inter", fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" },
    input: {
      fontFamily: "Inter", fontSize: 14, borderRadius: 10, border: "1.5px solid #E5E7EB",
      "&:focus": { borderColor: "#F59E0B" },
    },
  };

  const isEditing = !!editTask;
  const accentColor = "#F59E0B";
  const accentColorDark = "#D97706";
  const accentBg = "#FFFBEB";
  const accentTextDark = "#92400E";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={540}
      radius={16}
      padding={0}
      withCloseButton={false}
      styles={{
        body: { padding: 0 },
        content: { overflow: "hidden", borderRadius: 16 },
      }}
      overlayProps={{
        color: "#000",
        opacity: 0.5,
        blur: 4,
      }}
      zIndex={1000}
      overlayOpacity={0.5}
      overlayBlur={0.5}
      
    >
      {/* ── Gradient header ── */}
      <Box style={{
        background: "linear-gradient(135deg, #D97706, #F59E0B)",
        padding: "22px 28px 18px",
        position: "relative",
      }}>
        <Box
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 14,
            width: 30, height: 30, borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.35)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.2)")}
        >
          <Text fz={16} c="#fff" lh={1} fw={500}>&#x2715;</Text>
        </Box>
        <Flex align="center" gap={12}>
          <Box style={{
            width: 40, height: 40, borderRadius: 12,
            backgroundColor: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <IconClipboardCheck size={22} color="#fff" stroke={2} />
          </Box>
          <Box>
            <Text ff="Inter" fz={20} fw={800} c="#fff" lh={1.2}>
              {isEditing ? "Edit Task" : "Create New Task"}
            </Text>
            <Text ff="Inter" fz={12} c="rgba(255,255,255,0.7)" mt={2}>
              {isEditing ? "Update task details and assignments" : "Assign a task to your staff members"}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* ── Body ── */}
      <Box style={{
        padding: "20px 28px 24px", maxHeight: "calc(100vh - 280px)", overflowY: "auto",
        scrollbarWidth: "thin", scrollbarColor: "#D1D5DB transparent",
      }}>

        {/* Section: Task Type */}
        <Box mb={20}>
          <Flex align="center" gap={6} mb={12}>
            <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: accentColor }} />
            <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Task Type
            </Text>
          </Flex>
          <Flex gap={10} mb={taskType === "system" ? 14 : 0}>
            {[
              { value: "admin", label: "Admin", icon: <IconClipboardCheck size={16} />, desc: "Manual task tracked by admin" },
              { value: "system", label: "System", icon: <IconSettings size={16} />, desc: "Auto-completes when staff creates a note or task" },
            ].map((opt) => (
              <Box
                key={opt.value}
                onClick={() => { setTaskType(opt.value); if (opt.value === "admin") { setSystemCategory(null); setLinkedNoteType(null); setLinkedTaskType(null); } }}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                  border: `1.5px solid ${taskType === opt.value ? accentColor : "#E5E7EB"}`,
                  backgroundColor: taskType === opt.value ? accentBg : "#fff",
                  transition: "all 0.15s ease",
                }}
              >
                <Flex align="center" gap={8} mb={4}>
                  <Box style={{ color: taskType === opt.value ? accentColorDark : "#9CA3AF" }}>{opt.icon}</Box>
                  <Text ff="Inter" fz={13} fw={600} c={taskType === opt.value ? accentTextDark : "#6B7280"}>{opt.label}</Text>
                </Flex>
                <Text ff="Inter" fz={11} c="#9CA3AF" lh="16px">{opt.desc}</Text>
              </Box>
            ))}
          </Flex>

          {/* System sub-options */}
          {taskType === "system" && (
            <Box>
              <Flex gap={10} mb={systemCategory ? 14 : 0}>
                {[
                  { value: "note", label: "Note", icon: <IconNote size={16} />, desc: "Staff must create a note" },
                  { value: "task", label: "Task", icon: <IconSubtask size={16} />, desc: "Staff must create a task" },
                ].map((opt) => (
                  <Box
                    key={opt.value}
                    onClick={() => { setSystemCategory(opt.value); setLinkedNoteType(null); setLinkedTaskType(null); }}
                    style={{
                      flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                      border: `1.5px solid ${systemCategory === opt.value ? "#3B82F6" : "#E5E7EB"}`,
                      backgroundColor: systemCategory === opt.value ? "#EFF6FF" : "#fff",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Flex align="center" gap={8}>
                      <Box style={{ color: systemCategory === opt.value ? "#2563EB" : "#9CA3AF" }}>{opt.icon}</Box>
                      <Text ff="Inter" fz={13} fw={600} c={systemCategory === opt.value ? "#1E40AF" : "#6B7280"}>{opt.label}</Text>
                    </Flex>
                    <Text ff="Inter" fz={11} c="#9CA3AF" mt={2}>{opt.desc}</Text>
                  </Box>
                ))}
              </Flex>

              {/* Note Type selector */}
              {systemCategory === "note" && (
                <Select
                  label="Select Note Type"
                  placeholder="Choose a note type..."
                  searchable
                  data={noteTypesList.map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
                  value={linkedNoteType}
                  onChange={setLinkedNoteType}
                  styles={fieldStyles}
                />
              )}

              {/* Task Type selector */}
              {systemCategory === "task" && (
                <Select
                  label="Select Task Type"
                  placeholder="Choose a task type..."
                  searchable
                  data={taskTypesList.map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
                  value={linkedTaskType}
                  onChange={setLinkedTaskType}
                  styles={fieldStyles}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Section: Task Details */}
        <Box mb={20}>
          <Flex align="center" gap={6} mb={12}>
            <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: accentColor }} />
            <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Task Details
            </Text>
          </Flex>

          <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Title <span style={{ color: "#EF4444" }}>*</span></Text>
          <TextInput
            placeholder="What needs to be done?"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            mb={14}
            styles={{
              ...fieldStyles,
              input: {
                ...fieldStyles.input,
                fontSize: 15, fontWeight: 600, padding: "12px 14px", height: "auto",
                "&::placeholder": { fontWeight: 400, color: "#9CA3AF" },
              },
            }}
          />
          <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={6}>Description</Text>
          <Textarea
            placeholder="Provide details about this task — instructions, context, or expectations..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            mb={0}
            minRows={5}
            autosize
            maxRows={8}
            styles={{
              ...fieldStyles,
              input: {
                ...fieldStyles.input,
                fontSize: 13, lineHeight: 1.6,
                "&::placeholder": { color: "#BCBCBC" },
              },
            }}
          />
        </Box>

        {/* Section: Assign Staff */}
        <Box mb={20}>
          <Flex align="center" justify="space-between" mb={12}>
            <Flex align="center" gap={6}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: accentColor }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Assign Staff
              </Text>
              {selectedStaff.length > 0 && (
                <Box style={{
                  backgroundColor: accentColor, borderRadius: 20, height: 20, minWidth: 20,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
                }}>
                  <Text ff="Inter" fz={10} fw={700} c="#fff">{selectedStaff.length}</Text>
                </Box>
              )}
            </Flex>
            <Text
              fz={12} ff="Inter" fw={600}
              c={selectedStaff.length === staffData.length ? "#EF4444" : accentColorDark}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (selectedStaff.length === staffData.length) setSelectedStaff([]);
                else setSelectedStaff(staffData.map((s) => s.value));
              }}
            >
              {selectedStaff.length === staffData.length ? "Deselect All" : "Select All"}
            </Text>
          </Flex>

          <MultiSelect
            placeholder="Search and select staff members..."
            searchable
            data={staffData}
            value={selectedStaff}
            onChange={setSelectedStaff}
            mb={0}
            styles={{
              ...fieldStyles,
              input: { ...fieldStyles.input, minHeight: 42 },
              value: {
                backgroundColor: "#F3F4F6", borderRadius: 8,
                fontFamily: "Inter", fontSize: 12, fontWeight: 500,
              },
            }}
          />

          {/* Staff avatar preview chips */}
          {selectedStaff.length > 0 && (
            <Flex gap={6} mt={10} wrap="wrap">
              {selectedStaff.slice(0, 8).map((id) => {
                const staffMember = staffData.find((s) => s.value === id);
                if (!staffMember) return null;
                const color = getAvatarColor(staffMember.label);
                return (
                  <Flex
                    key={id}
                    align="center"
                    gap={6}
                    style={{
                      backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB",
                      borderRadius: 20, padding: "4px 10px 4px 4px",
                    }}
                  >
                    <Box style={{
                      width: 22, height: 22, borderRadius: "50%", backgroundColor: color,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Text ff="Inter" fz={9} fw={700} c="#fff">{getInitials(staffMember.label)}</Text>
                    </Box>
                    <Text ff="Inter" fz={11} fw={500} c="#374151">{staffMember.label}</Text>
                  </Flex>
                );
              })}
              {selectedStaff.length > 8 && (
                <Box style={{
                  backgroundColor: "#F3F4F6", borderRadius: 20, padding: "4px 10px",
                  display: "flex", alignItems: "center",
                }}>
                  <Text ff="Inter" fz={11} fw={600} c="#6B7280">+{selectedStaff.length - 8} more</Text>
                </Box>
              )}
            </Flex>
          )}
        </Box>

        {/* Section: Schedule */}
        <Box mb={20}>
          <Flex align="center" gap={6} mb={12}>
            <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: accentColor }} />
            <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Schedule
            </Text>
          </Flex>

          {/* Schedule type toggle cards */}
          <Flex gap={10} mb={16}>
            <Box
              onClick={() => setScheduleType("one_time")}
              style={{
                flex: 1, padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                border: `2px solid ${scheduleType === "one_time" ? accentColor : "#E5E7EB"}`,
                backgroundColor: scheduleType === "one_time" ? accentBg : "#FAFAFA",
                transition: "all 0.2s ease",
              }}
            >
              <Flex align="center" gap={10}>
                <Box style={{
                  width: 36, height: 36, borderRadius: 10,
                  backgroundColor: scheduleType === "one_time" ? accentColor : "#E5E7EB",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background-color 0.2s ease",
                }}>
                  <IconCalendar size={18} color={scheduleType === "one_time" ? "#fff" : "#9CA3AF"} stroke={2} />
                </Box>
                <Box>
                  <Text ff="Inter" fz={14} fw={700} c={scheduleType === "one_time" ? accentTextDark : "#374151"}>
                    One Time
                  </Text>
                  <Text ff="Inter" fz={11} c={scheduleType === "one_time" ? accentColorDark : "#9CA3AF"} fw={500}>
                    Single deadline
                  </Text>
                </Box>
              </Flex>
            </Box>

            <Box
              onClick={() => setScheduleType("recurring")}
              style={{
                flex: 1, padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                border: `2px solid ${scheduleType === "recurring" ? accentColor : "#E5E7EB"}`,
                backgroundColor: scheduleType === "recurring" ? accentBg : "#FAFAFA",
                transition: "all 0.2s ease",
              }}
            >
              <Flex align="center" gap={10}>
                <Box style={{
                  width: 36, height: 36, borderRadius: 10,
                  backgroundColor: scheduleType === "recurring" ? accentColor : "#E5E7EB",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background-color 0.2s ease",
                }}>
                  <IconListCheck size={18} color={scheduleType === "recurring" ? "#fff" : "#9CA3AF"} stroke={2} />
                </Box>
                <Box>
                  <Text ff="Inter" fz={14} fw={700} c={scheduleType === "recurring" ? accentTextDark : "#374151"}>
                    Recurring
                  </Text>
                  <Text ff="Inter" fz={11} c={scheduleType === "recurring" ? accentColorDark : "#9CA3AF"} fw={500}>
                    Daily, weekly, monthly
                  </Text>
                </Box>
              </Flex>
            </Box>
          </Flex>

          {/* Recurring config */}
          {scheduleType === "recurring" && (
            <Box style={{
              backgroundColor: "#F9FAFB", borderRadius: 12, padding: 16,
              border: "1px solid #F3F4F6",
            }}>
              <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={8}>Frequency</Text>
              <Flex gap={8} mb={14}>
                {[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ].map((f) => (
                  <Box
                    key={f.value}
                    onClick={() => setFrequency(f.value)}
                    style={{
                      padding: "7px 18px", borderRadius: 20, cursor: "pointer",
                      border: `1.5px solid ${frequency === f.value ? accentColor : "#E5E7EB"}`,
                      backgroundColor: frequency === f.value ? accentColor : "#fff",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Text ff="Inter" fz={12} fw={600} c={frequency === f.value ? "#fff" : "#6B7280"}>
                      {f.label}
                    </Text>
                  </Box>
                ))}
              </Flex>

              {frequency === "weekly" && (
                <Box mb={14}>
                  <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={8}>Day of Week</Text>
                  <Flex gap={6} wrap="wrap">
                    {[
                      { value: 0, label: "Sun" }, { value: 1, label: "Mon" },
                      { value: 2, label: "Tue" }, { value: 3, label: "Wed" },
                      { value: 4, label: "Thu" }, { value: 5, label: "Fri" },
                      { value: 6, label: "Sat" },
                    ].map((d) => (
                      <Box
                        key={d.value}
                        onClick={() => setDayOfWeek(d.value)}
                        style={{
                          width: 44, height: 36, borderRadius: 8, cursor: "pointer",
                          border: `1.5px solid ${dayOfWeek === d.value ? accentColor : "#E5E7EB"}`,
                          backgroundColor: dayOfWeek === d.value ? accentColor : "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <Text ff="Inter" fz={11} fw={600} c={dayOfWeek === d.value ? "#fff" : "#6B7280"}>
                          {d.label}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </Box>
              )}

              {frequency === "monthly" && (
                <NumberInput
                  label="Day of Month"
                  required
                  min={1}
                  max={31}
                  value={dayOfMonth}
                  onChange={setDayOfMonth}
                  mb={14}
                  styles={fieldStyles}
                />
              )}

              <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={8}>Time Due</Text>
              <Flex gap={10}>
                <Select
                  placeholder="Time"
                  data={[
                    "12:00", "12:30", "01:00", "01:30", "02:00", "02:30",
                    "03:00", "03:30", "04:00", "04:30", "05:00", "05:30",
                    "06:00", "06:30", "07:00", "07:30", "08:00", "08:30",
                    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
                  ].map((t) => ({ value: t, label: t }))}
                  value={timeOfDay.replace(/^(\d{2}:\d{2}).*/, "$1")}
                  onChange={(v) => {
                    const period = timeOfDay.includes("PM") ? "PM" : "AM";
                    setTimeOfDay(`${v} ${period}`);
                  }}
                  styles={fieldStyles}
                  searchable
                  style={{ flex: 1 }}
                  radius="md"
                />
                <Flex gap={0} style={{ borderRadius: 10, overflow: "hidden", border: "1.5px solid #E5E7EB" }}>
                  <Box
                    onClick={() => {
                      const timePart = timeOfDay.replace(/\s*(AM|PM)\s*/i, "").trim();
                      setTimeOfDay(`${timePart} AM`);
                    }}
                    style={{
                      padding: "8px 16px", cursor: "pointer",
                      backgroundColor: !timeOfDay.includes("PM") ? accentColor : "#fff",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Text ff="Inter" fz={13} fw={600} c={!timeOfDay.includes("PM") ? "#fff" : "#6B7280"}>AM</Text>
                  </Box>
                  <Box
                    onClick={() => {
                      const timePart = timeOfDay.replace(/\s*(AM|PM)\s*/i, "").trim();
                      setTimeOfDay(`${timePart} PM`);
                    }}
                    style={{
                      padding: "8px 16px", cursor: "pointer",
                      backgroundColor: timeOfDay.includes("PM") ? accentColor : "#fff",
                      borderLeft: "1.5px solid #E5E7EB",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Text ff="Inter" fz={13} fw={600} c={timeOfDay.includes("PM") ? "#fff" : "#6B7280"}>PM</Text>
                  </Box>
                </Flex>
              </Flex>
            </Box>
          )}

          {/* One-time config */}
          {scheduleType === "one_time" && (
            <Box style={{
              backgroundColor: "#F9FAFB", borderRadius: 12, padding: 16,
              border: "1px solid #F3F4F6",
            }}>
              <Text ff="Inter" fz={12} fw={600} c="#6B7280" mb={8}>Quick Set</Text>
              <Flex gap={8} mb={14}>
                {[
                  { label: "Today", fn: () => setDueDate(new Date()) },
                  { label: "Tomorrow", fn: () => { const d = new Date(); d.setDate(d.getDate() + 1); setDueDate(d); } },
                  { label: "Next Week", fn: () => { const d = new Date(); d.setDate(d.getDate() + 7); setDueDate(d); } },
                  { label: "Next Month", fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 1); setDueDate(d); } },
                ].map((q) => (
                  <Box
                    key={q.label}
                    onClick={q.fn}
                    style={{
                      padding: "6px 14px", borderRadius: 20, cursor: "pointer",
                      border: "1.5px solid #E5E7EB", backgroundColor: "#fff",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F59E0B"; e.currentTarget.style.backgroundColor = "#FFFBEB"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.backgroundColor = "#fff"; }}
                  >
                    <Text ff="Inter" fz={12} fw={600} c="#374151">{q.label}</Text>
                  </Box>
                ))}
              </Flex>

              <DatePickerInput
                label="Due Date"
                placeholder="Select due date"
                required
                value={dueDate}
                onChange={setDueDate}
                minDate={new Date()}
                styles={fieldStyles}
                radius="md"
              />
              {dueDate && (
                <Text ff="Inter" fz={11} c={accentColorDark} fw={500} mt={6}>
                  {moment(dueDate).format("dddd, MMMM D, YYYY")}
                </Text>
              )}
            </Box>
          )}
        </Box>

        {/* Section: Status (edit mode only) */}
        {isEditing && (
          <Box mb={10}>
            <Flex align="center" gap={6} mb={12}>
              <Box style={{ width: 3, height: 16, borderRadius: 3, backgroundColor: accentColor }} />
              <Text ff="Inter" fz={13} fw={700} c="#374151" style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Status
              </Text>
            </Flex>
            <Flex gap={8}>
              {[
                { value: "not_started", label: "Not Started", color: "#6B7280", bg: "#F3F4F6", border: "#D1D5DB" },
                { value: "in_progress", label: "In Progress", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
                { value: "complete", label: "Complete", color: "#065F46", bg: "#D1FAE5", border: "#A7F3D0" },
              ].map((s) => (
                <Box
                  key={s.value}
                  onClick={() => setStatus(s.value)}
                  style={{
                    flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                    border: `2px solid ${status === s.value ? s.border : "#E5E7EB"}`,
                    backgroundColor: status === s.value ? s.bg : "#FAFAFA",
                    textAlign: "center",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Text ff="Inter" fz={13} fw={600} c={status === s.value ? s.color : "#9CA3AF"}>
                    {s.label}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box style={{
        padding: "16px 28px 20px",
        borderTop: "1px solid #F3F4F6",
        backgroundColor: "#FAFAFA",
      }}>
        <Flex justify="space-between" align="center">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            radius={10}
            styles={{
              root: { fontFamily: "Inter", fontWeight: 600, fontSize: 14, color: "#6B7280", padding: "8px 20px" },
            }}
          >
            Cancel
          </Button>
          <Button
            loading={addLoading}
            onClick={handleSubmit}
            radius={10}
            styles={{
              root: {
                fontFamily: "Inter", fontWeight: 700, fontSize: 14,
                background: "linear-gradient(135deg, #D97706, #F59E0B)",
                border: "none", padding: "10px 32px", height: "auto",
                "&:hover": {
                  background: "linear-gradient(135deg, #B45309, #D97706)",
                },
              },
            }}
          >
            {isEditing ? "Update Task" : "Create Task"}
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export default ComplianceTaskModal;
